import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { teamChatAPI, teamAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Send, Loader2, Users, AtSign, Plus, Hash, Lock, Trash2, Settings } from 'lucide-react';
import { format } from 'date-fns';

const TeamChat = () => {
  const [messages, setMessages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null); // null = general
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [createChannelDialog, setCreateChannelDialog] = useState(false);
  const [newChannel, setNewChannel] = useState({ name: '', members: [], isPrivate: true });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchTeamMembers();
    fetchChannels();
    fetchMessages();
    const interval = setInterval(() => fetchMessages(), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [activeChannel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await teamChatAPI.getChannels();
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await teamChatAPI.getMessages(50, activeChannel);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await teamChatAPI.sendMessage(newMessage, activeChannel);
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    }
  };

  const handleCreateChannel = async () => {
    if (!newChannel.name.trim()) {
      toast({ title: 'Error', description: 'Channel name is required', variant: 'destructive' });
      return;
    }

    try {
      await teamChatAPI.createChannel(newChannel);
      toast({ title: 'Success', description: 'Channel created' });
      setCreateChannelDialog(false);
      setNewChannel({ name: '', members: [], isPrivate: true });
      fetchChannels();
    } catch (error) {
      console.error('Error creating channel:', error);
      toast({ title: 'Error', description: 'Failed to create channel', variant: 'destructive' });
    }
  };

  const handleDeleteChannel = async (channelId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this channel? All messages will be lost.')) return;

    try {
      await teamChatAPI.deleteChannel(channelId);
      toast({ title: 'Success', description: 'Channel deleted' });
      if (activeChannel === channelId) {
        setActiveChannel(null);
      }
      fetchChannels();
    } catch (error) {
      console.error('Error deleting channel:', error);
      toast({ title: 'Error', description: 'Failed to delete channel', variant: 'destructive' });
    }
  };

  const toggleMember = (memberName) => {
    setNewChannel(prev => ({
      ...prev,
      members: prev.members.includes(memberName)
        ? prev.members.filter(m => m !== memberName)
        : [...prev.members, memberName]
    }));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    setNewMessage(value);
    setCursorPosition(position);
    
    const textBeforeCursor = value.slice(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setShowMentions(true);
        return;
      }
    }
    
    setShowMentions(false);
    setMentionFilter('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
      e.preventDefault();
      handleSendMessage();
    }
    if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const insertMention = (memberName) => {
    const textBeforeCursor = newMessage.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = newMessage.slice(cursorPosition);
    
    const newText = textBeforeCursor.slice(0, lastAtIndex) + `@${memberName} ` + textAfterCursor;
    setNewMessage(newText);
    setShowMentions(false);
    setMentionFilter('');
    inputRef.current?.focus();
  };

  const filteredMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(mentionFilter)
  );

  const formatMessage = (text) => {
    const mentionRegex = /@(\w+\s\w+)/g;
    const parts = text.split(mentionRegex);
    
    return parts.map((part, index) => {
      const isMention = teamMembers.some(m => m.name === part);
      if (isMention) {
        return (
          <span key={index} className="bg-blue-100 text-blue-800 px-1 rounded font-medium">
            @{part}
          </span>
        );
      }
      return part;
    });
  };

  const getMemberInfo = (sender) => {
    if (!sender) return { name: 'Unknown', initials: '?', color: 'from-gray-500 to-gray-600' };
    const member = teamMembers.find(m => m.name === sender);
    const colors = ['from-pink-500 to-rose-600', 'from-blue-500 to-cyan-600', 'from-purple-500 to-indigo-600', 'from-green-500 to-emerald-600'];
    const colorIndex = sender.charCodeAt(0) % colors.length;
    return {
      name: member?.name || sender,
      initials: sender.split(' ').map(n => n?.[0] || '').join('').toUpperCase(),
      color: colors[colorIndex]
    };
  };

  const getActiveChannelName = () => {
    if (!activeChannel) return 'General';
    const channel = channels.find(c => c._id === activeChannel);
    return channel?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Chat</h1>
          <p className="text-gray-500 mt-1">Internal team communication</p>
        </div>
        <Button onClick={() => setCreateChannelDialog(true)} className="gap-2" data-testid="create-channel-btn">
          <Plus className="h-4 w-4" />
          New Channel
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Channels Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Channels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* General Channel */}
            <div
              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                activeChannel === null ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
              }`}
              onClick={() => setActiveChannel(null)}
              data-testid="general-channel"
            >
              <Hash className="h-4 w-4" />
              <span className="font-medium">General</span>
            </div>

            {/* Private Channels */}
            {channels.length > 0 && (
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Private Channels</p>
                <div className="space-y-1">
                  {channels.map((channel) => (
                    <div
                      key={channel._id}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors group ${
                        activeChannel === channel._id ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => setActiveChannel(channel._id)}
                      data-testid={`channel-${channel._id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        <span className="font-medium text-sm">{channel.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={(e) => handleDeleteChannel(channel._id, e)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team Members */}
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Team Members</p>
              <div className="space-y-2">
                {teamMembers.map((member) => {
                  const info = getMemberInfo(member.name);
                  return (
                    <div key={member._id} className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className={`bg-gradient-to-br ${info.color} text-white text-xs`}>
                          {info.initials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{member.name}</span>
                      <div className="w-2 h-2 rounded-full bg-green-500 ml-auto" />
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-3 flex flex-col h-[calc(100vh-240px)]">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              {activeChannel ? <Lock className="h-5 w-5" /> : <Hash className="h-5 w-5" />}
              {getActiveChannelName()}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {messages.map((msg) => {
                const member = getMemberInfo(msg.sender || msg.author);
                return (
                  <div key={msg._id} className="flex items-start gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={`bg-gradient-to-br ${member.color} text-white text-xs`}>
                        {member.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm" data-testid="message-author">
                          {msg.sender || msg.author}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(msg.createdAt || msg.timestamp) && format(new Date(msg.createdAt || msg.timestamp), 'MMM dd, h:mm a')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">
                        {formatMessage(msg.message)}
                      </p>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No messages yet. Start the conversation!
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>
          
          {/* Message Input */}
          <div className="p-4 border-t relative">
            {showMentions && filteredMembers.length > 0 && (
              <div className="absolute bottom-full left-4 right-4 mb-2 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredMembers.map((member) => {
                  const info = getMemberInfo(member.name);
                  return (
                    <div
                      key={member._id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => insertMention(member.name)}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className={`bg-gradient-to-br ${info.color} text-white text-xs`}>
                          {info.initials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-sm">{member.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  placeholder="Type a message... (use @ to mention someone)"
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className="pr-10"
                  data-testid="chat-input"
                />
                <AtSign 
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600"
                  onClick={() => {
                    setNewMessage(prev => prev + '@');
                    setShowMentions(true);
                    inputRef.current?.focus();
                  }}
                />
              </div>
              <Button onClick={handleSendMessage} className="gap-2" data-testid="send-message-btn">
                <Send className="h-4 w-4" />
                Send
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Type @ to mention a team member
            </p>
          </div>
        </Card>
      </div>

      {/* Create Channel Dialog */}
      <Dialog open={createChannelDialog} onOpenChange={setCreateChannelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Private Channel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Channel Name *</Label>
              <Input
                placeholder="e.g., Project Alpha"
                value={newChannel.name}
                onChange={(e) => setNewChannel(prev => ({ ...prev, name: e.target.value }))}
                data-testid="channel-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Add Members</Label>
              <p className="text-xs text-gray-500 mb-2">Select team members who can access this channel</p>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {teamMembers.map((member) => {
                  const info = getMemberInfo(member.name);
                  return (
                    <div key={member._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
                      <Checkbox
                        id={`member-${member._id}`}
                        checked={newChannel.members.includes(member.name)}
                        onCheckedChange={() => toggleMember(member.name)}
                      />
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className={`bg-gradient-to-br ${info.color} text-white text-xs`}>
                          {info.initials}
                        </AvatarFallback>
                      </Avatar>
                      <Label htmlFor={`member-${member._id}`} className="cursor-pointer">
                        {member.name}
                      </Label>
                    </div>
                  );
                })}
              </div>
              {newChannel.members.length > 0 && (
                <p className="text-xs text-gray-500">
                  {newChannel.members.length} member(s) selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateChannelDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateChannel} data-testid="create-channel-submit">
              Create Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamChat;
